import { fabric } from 'fabric';
import ControlBase from '../fabric/ControlBase';
import { renderSplitControl } from './controlRendering';
import { sqrDistanceBetweenPoints } from '../utils/geometry';
import type Connector from '../objects/Connector';
import RouterOrthogonal from '../objects/connectorRouters/RouterOrthogonal';
import { type IPoint, type Transform } from 'fabric/fabric-impl';
import { ConnectorRouting } from '../objects/ConnectorRouting';

// Amount of movement required before we consider the user is trying to split
// the segment.
const MIN_MOVEMENT = 10;
// Minimum segment length in order to show the split control.
const MIN_SEGMENT_LENGTH = 80;
const MIN_ORTHOGONAL_SEGMENT_LENGTH = 30;

// Proximity necessary for orthogonal segment to snap.
const SNAP_DISTANCE = 8;

// Control handle splitting a connector into more segments.
class ConnectorSplitControl extends ControlBase {
  actionName = 'splitConnector';
  minDimension = 0;
  afterIndex: number;
  moveHorizontally: boolean;
  changed = false;
  dragStart: { x: number; y: number };

  constructor(fabricObject: Connector, afterIndex: number) {
    super({});
    this.afterIndex = afterIndex;

    // Set the appropriate cursor.
    if (
      fabricObject.lineRouting === ConnectorRouting.ROUTING_ORTHOGONAL_AUTO ||
      fabricObject.lineRouting === ConnectorRouting.ROUTING_ORTHOGONAL_MANUAL
    ) {
      this.moveHorizontally = !RouterOrthogonal.segmentHorizontal(
        fabricObject,
        afterIndex
      );

      this.cursorStyle = this.moveHorizontally ? 'ew-resize' : 'ns-resize';
    }
  }

  splitConnector(eventData: MouseEvent, fabricObject: Connector) {
    // Add a point to the connector.
    fabricObject.addPointAfter(this.afterIndex);

    // Convert this control handle into a point control to continue dragging.
    fabricObject.__corner = `p${this.afterIndex + 1}`;
    fabricObject.canvas._setupCurrentTransform(eventData, fabricObject, true);
  }

  getMouseDownHandler(eventData: MouseEvent, fabricObject: Connector) {
    return (e: MouseEvent, transform: Transform, x: number, y: number) => {
      fabricObject.canvas.controller.actionHideObjectToolbar();
      fabricObject.beingControlled = true;
      this.dragStart = { x, y };
      this.changed = false;
      return false;
    };
  }

  getMouseUpHandler(eventData: MouseEvent, fabricObject: Connector) {
    return () => {
      if (
        fabricObject.lineRouting === ConnectorRouting.ROUTING_ORTHOGONAL_MANUAL
      ) {
        let mergedSegments = false;

        // If any points are coincident it means the length of a segment is zero.
        // Remove that segment that merge the two on either side. We use the
        // while loop in case a merged segment can be merged again with the next segment.
        let i = 0;
        while (i < fabricObject.points.length - 2) {
          const point0 = fabricObject.points[i];
          const point1 = fabricObject.points[i + 1];
          const point2 = fabricObject.points[i + 2];

          if (i === 0 && point0.x === point1.x && point0.y === point1.y) {
            fabricObject.points.splice(1, 1);
            mergedSegments = true;
          } else if (
            i === fabricObject.points.length - 3 &&
            point1.x === point2.x &&
            point1.y === point2.y
          ) {
            fabricObject.points.splice(i + 1, 1);
            mergedSegments = true;
          } else if (point1.x === point2.x && point1.y === point2.y) {
            fabricObject.points.splice(i + 1, 2);
            mergedSegments = true;
          } else {
            i++;
          }
        }

        // each point must share a coordinate with the previous - enforce that
        const points = fabricObject.points;
        let shouldMatch = points[0].x === points[1].x ? 'x' : 'y';
        for (i = 0; i < points.length - 1; i++) {
          const matchX = points[i].x === points[i + 1].x;
          const matchY = points[i].y === points[i + 1].y;
          if (!matchX && !matchY) {
            points[i + 1][shouldMatch] = points[i][shouldMatch];
            mergedSegments = true;
            shouldMatch = shouldMatch === 'x' ? 'y' : 'x';
          } else {
            shouldMatch = matchY ? 'x' : 'y';
          }
        }

        if (mergedSegments) {
          fabricObject.set('points', fabricObject.points);
          fabricObject.createControls();
          fabricObject.canvas.requestRenderAll();
          this.changed = true;
        }
      }

      if (this.changed) {
        fabricObject.controller.undoManager.completeUndoGroup();
      }
      fabricObject.beingControlled = false;
      fabricObject.synchronize();
      fabricObject.canvas.controller.actionShowObjectToolbar();

      return false;
    };
  }

  getActionHandler(eventData: MouseEvent, fabricObject: Connector) {
    return (
      eventData: MouseEvent,
      transform: Transform,
      x: number,
      y: number
    ) => {
      if (
        fabricObject.lineRouting === ConnectorRouting.ROUTING_ORTHOGONAL_AUTO
      ) {
        // Convert the connector to manual mode.
        fabricObject.set({
          lineRouting: ConnectorRouting.ROUTING_ORTHOGONAL_MANUAL,
          points: fabricObject.controlPoints,
        });
        fabricObject.createControls();
        this.changed = true;
      }

      if (
        fabricObject.lineRouting === ConnectorRouting.ROUTING_ORTHOGONAL_MANUAL
      ) {
        let pointBefore = this.point(fabricObject, this.afterIndex);
        let pointAfter = this.point(fabricObject, this.afterIndex + 1);

        // When moving the first or last segments, add another segment.
        if (
          this.afterIndex === 0 ||
          this.afterIndex === fabricObject.points.length - 2
        ) {
          let newPoint: IPoint;
          const horizontal = RouterOrthogonal.segmentHorizontal(
            fabricObject,
            this.afterIndex
          );

          // Special case for a single segment connector.
          if (fabricObject.points.length === 2) {
            fabricObject.points.splice(
              this.afterIndex + 1,
              0,
              { ...pointBefore },
              { ...pointAfter }
            );
            this.afterIndex += 1;
          } else {
            if (
              this.afterIndex === fabricObject.points.length - 2
                ? !horizontal
                : horizontal
            ) {
              newPoint = {
                x: pointBefore.x,
                y: pointAfter.y,
              };
            } else {
              newPoint = {
                x: pointAfter.x,
                y: pointBefore.y,
              };
            }
            fabricObject.points.splice(this.afterIndex + 1, 0, newPoint);
            if (this.afterIndex === 0) {
              this.afterIndex += 1;
            }
          }

          // Re-select the points to drag since they changed.
          pointBefore = this.point(fabricObject, this.afterIndex);
          pointAfter = this.point(fabricObject, this.afterIndex + 1);

          // Recreate the controls since the number of points in the line changed.
          fabricObject.createControls();
          this.changed = true;
        }

        // If lines are aligned snap to be in line. We consider any point along the line.
        for (let i = 0; i < fabricObject.points.length; i++) {
          // Don't align the segment we are moving with itself.
          if (i === this.afterIndex || i === this.afterIndex + 1) continue;

          const point = fabricObject.points[i];
          const snapDistance = SNAP_DISTANCE / fabricObject.canvas.getZoom();

          if (Math.abs(point.x - x) < snapDistance) x = point.x;
          if (Math.abs(point.y - y) < snapDistance) y = point.y;
        }

        // Move the line segment.
        if (this.moveHorizontally) {
          pointBefore.x = x;
          pointAfter.x = x;
        } else {
          pointBefore.y = y;
          pointAfter.y = y;
        }

        fabricObject.set('points', fabricObject.points);
        fabricObject.canvas.requestRenderAll();
        this.changed = true;
      } else {
        // See if we have moved enough yet to trigger the split. We require a
        // minimum movement so that just clicking the handle (like when selecting
        // the connector) can't cause a split.
        if (
          Math.abs(x - this.dragStart.x) * fabricObject.canvas.getZoom() >
            MIN_MOVEMENT ||
          Math.abs(y - this.dragStart.y) * fabricObject.canvas.getZoom() >
            MIN_MOVEMENT
        ) {
          this.splitConnector(eventData, fabricObject);
        }
      }

      return false;
    };
  }

  point(fabricObject: Connector, index: number) {
    if (fabricObject.lineRouting === ConnectorRouting.ROUTING_ORTHOGONAL_AUTO) {
      if (fabricObject.controlPoints.length === 0) {
        return { x: 0, y: 0 };
      } else {
        return fabricObject.controlPoints[index];
      }
    } else {
      return fabricObject.points[index];
    }
  }

  minSegmentLength(fabricObject: Connector) {
    if (
      fabricObject.lineRouting === ConnectorRouting.ROUTING_ORTHOGONAL_AUTO ||
      fabricObject.lineRouting === ConnectorRouting.ROUTING_ORTHOGONAL_MANUAL
    ) {
      return MIN_ORTHOGONAL_SEGMENT_LENGTH;
    } else {
      return MIN_SEGMENT_LENGTH;
    }
  }

  // Position the control handle. It is only visible if the segment is
  // long enough so that we reduce possibility of user hitting it by mistake.
  positionHandler(dim, finalMatrix, fabricObject) {
    const endPoint = this.point(fabricObject, this.afterIndex + 1);
    const lineLength = sqrDistanceBetweenPoints(
      this.point(fabricObject, this.afterIndex),
      endPoint.x,
      endPoint.y
    );
    const scaledSegmentLength =
      this.minSegmentLength(fabricObject) / fabricObject.canvas.getZoom();
    this.setVisibility(lineLength > scaledSegmentLength * scaledSegmentLength);
    const midPoint = fabricObject.midPoint(this.afterIndex);
    midPoint.x -= fabricObject.pathOffset.x;
    midPoint.y -= fabricObject.pathOffset.y;

    return fabric.util.transformPoint(
      midPoint,
      fabric.util.multiplyTransformMatrices(
        fabricObject.canvas.viewportTransform,
        fabricObject.calcTransformMatrix()
      )
    );
  }

  render(
    ctx: CanvasRenderingContext2D,
    left: number,
    top: number,
    styleOverride: unknown,
    fabricObject: Connector
  ) {
    if (!fabricObject.beingControlled) {
      renderSplitControl.call(
        this,
        ctx,
        left,
        top,
        styleOverride,
        fabricObject
      );
    }
  }
}

export default ConnectorSplitControl;
